import { useLingui } from '@lingui/react/macro';
import { TriangleAlert } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { callDetectingLostPromise } from 'shared/utils/request-tracker';
import UDButton from '../button';
import UDText from '../text';
import UDModal, { UDModalSize } from './modal';
import { ModalFooter, ModalHeader } from './modal-parts';

/**
 * Тон окна подтверждения. Критерий — необратимость по тексту самого окна, а не слово «Удалить»:
 * - `danger` — после действия данные не вернуть (удалить, исключить, перезаписать, уйти с потерей правок);
 * - `neutral` — ничего не теряется (сохранить, отключить то, что можно подключить снова).
 */
export type UDConfirmModalTone = 'danger' | 'neutral';

export interface UDConfirmModalProps {
  show: boolean;

  /** Вопрос по канону: «Удалить команду «Метеор»?» */
  title: string;
  /** Пояснение под заголовком. Обычно «Это действие необратимо.» */
  description?: string;

  /** Подпись кнопки действия — глагол: «Удалить», «Исключить», «Покинуть». */
  confirmText?: string;
  cancelText?: string;

  /** `danger` (по умолчанию) — красная кнопка действия и пояснение блоком предупреждения; `neutral` — жёлтая кнопка и серый подзаголовок. */
  tone?: UDConfirmModalTone;

  /**
   * Только когда запрос идёт **не** из `onConfirm`. Промис `onConfirm` запирает обе кнопки окна сам
   * (IMGM-1561, `.claude/docs/ud-ui-gotchas.md`).
   */
  isLoading?: boolean;

  onConfirm: () => void | Promise<unknown>;
  /** Закрытие окна: и кнопкой отмены, и крестиком, и Escape, и кликом по затемнению. */
  onCancel: () => void;

  size?: UDModalSize;

  disableEscapeKey?: boolean;
  disableOverlayClick?: boolean;
}

/**
 * Каноническое окно подтверждения: заголовок по центру, «Отмена» (secondary) слева,
 * действие справа, обе на пол-ширины.
 *
 * Тон меняет ровно две вещи: вариант кнопки действия (`danger` / `primary`) и вид пояснения
 * (блок предупреждения / серый подзаголовок). Раскладка и порядок кнопок у тонов общие.
 * Слота для произвольного тела нет намеренно: предупреждение рисует кит, а не экран.
 *
 * Окно НЕ закрывает себя после `onConfirm` — закрывает вызывающая сторона,
 * когда операция действительно удалась. Иначе окно исчезало бы и при ошибке:
 * обработчики удаления ловят ошибку сами и возвращают выполненный промис.
 */
const UDConfirmModal: React.FC<UDConfirmModalProps> = ({
  show,
  title,
  description,
  confirmText,
  cancelText,
  tone = 'danger',
  isLoading,
  onConfirm,
  onCancel,
  size = 'sm',
  disableEscapeKey,
  disableOverlayClick,
}) => {
  const { t } = useLingui();
  // Кнопка заперта, пока жив промис `onConfirm`; обработчик, потерявший промис запроса, dev-детектор
  // называет в консоли (`shared/utils/request-tracker.ts`) — сам `handleConfirm` промис возвращает всегда.
  // «Отмена» гаснет вместе с кнопкой действия: закрыть окно посреди запроса — значит не узнать его исход.
  const [isConfirming, setIsConfirming] = useState(false);
  const handleConfirm = useCallback(async () => {
    const result = callDetectingLostPromise(onConfirm, () => `Modal.Confirm «${title}»`);
    if (typeof (result as Promise<unknown> | undefined)?.then !== 'function') return;
    setIsConfirming(true);
    try {
      await result;
    } finally {
      setIsConfirming(false);
    }
  }, [onConfirm, title]);

  const isDanger = tone === 'danger';
  const isBusy = Boolean(isLoading) || isConfirming;
  // Крестик, Escape и клик по затемнению на время запроса тоже ничего не делают: закрытое окно с живой строкой
  // позволяло снова нажать «Удалить» и отправить второй запрос. `disableEscapeKey` здесь не годится — он снимает
  // окно со стека модалок, и Escape закрыл бы окно под ним.
  const handleClose = useCallback(() => {
    if (!isBusy) onCancel();
  }, [isBusy, onCancel]);

  return (
    <UDModal show={show} onClose={handleClose} size={size} disableEscapeKey={disableEscapeKey} disableOverlayClick={disableOverlayClick}>
      <ModalHeader title={title} subtitle={isDanger ? undefined : description} />
      {isDanger && description && (
        <div
          data-testid="confirm-warning"
          className="rounded-card bg-CaseErrorSurface text-CaseErrorText mt-4 flex items-start gap-2 p-3"
        >
          {/* Иконка — в высоту первой строки (14px × 1.35 у relaxed): так она стоит вровень и с одной строкой, и с абзацем. */}
          <TriangleAlert size={18} className="shrink-0" aria-hidden />
          <UDText variant="text-2" relaxed className="text-CaseErrorText whitespace-pre-wrap">
            {description}
          </UDText>
        </div>
      )}
      <ModalFooter>
        <UDButton variant="secondary" onClick={onCancel} disabled={isBusy} stretch>
          {cancelText ?? t`Отмена`}
        </UDButton>
        <UDButton variant={isDanger ? 'danger' : 'primary'} onClick={handleConfirm} loading={Boolean(isLoading)} stretch>
          {confirmText ?? t`Удалить`}
        </UDButton>
      </ModalFooter>
    </UDModal>
  );
};

export default UDConfirmModal;
